import React, { useEffect, useState } from 'react';
import {
  Box, Typography, IconButton, Button, TextField, Grid,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress, Alert,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  Stack, Chip, Checkbox, FormControlLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  updatePatient, createPatientInsurance, updatePatientInsurance, deletePatientInsurance,
} from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../hooks/useNotification';
import { GENDER_OPTIONS, BLOOD_TYPES } from './patientProfileHelpers';

// ── Coberturas del paciente (obra social / prepaga) — un paciente puede
// tener varias, una marcada como principal. Pedido explícito del usuario:
// "hay pacientes con más de 1". ──────────────────────────────────────────
const InsurancesSection = ({ patient, canEdit, onChanged }) => {
  const notify = useNotification();
  const [adding, setAdding] = useState(false);
  const [newForm, setNewForm] = useState({ provider: '', policy_number: '', plan: '', is_primary: false });
  const [savingNew, setSavingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEditId, setSavingEditId] = useState(null);

  const insurances = patient.insurances || [];

  const startEdit = (ins) => {
    setEditingId(ins.id);
    setEditForm({ provider: ins.provider, policy_number: ins.policy_number || '', plan: ins.plan || '', is_primary: ins.is_primary });
  };

  const handleAdd = async () => {
    if (!newForm.provider.trim()) return;
    setSavingNew(true);
    try {
      await createPatientInsurance(patient.id, newForm);
      setAdding(false);
      setNewForm({ provider: '', policy_number: '', plan: '', is_primary: false });
      onChanged();
    } catch (err) {
      notify(err.userMessage || 'Error al agregar la cobertura', 'error');
    } finally {
      setSavingNew(false);
    }
  };

  const handleSaveEdit = async (id) => {
    setSavingEditId(id);
    try {
      await updatePatientInsurance(patient.id, id, editForm);
      setEditingId(null);
      onChanged();
    } catch (err) {
      notify(err.userMessage || 'Error al guardar', 'error');
    } finally {
      setSavingEditId(null);
    }
  };

  const handleDelete = async (ins) => {
    if (!window.confirm(`¿Quitar la cobertura "${ins.provider}"?`)) return;
    try {
      await deletePatientInsurance(patient.id, ins.id);
      onChanged();
    } catch (err) {
      notify(err.userMessage || 'Error al eliminar la cobertura', 'error');
    }
  };

  return (
    <Box>
      {insurances.length === 0 && !adding && (
        // Fallback: paciente viejo que solo tiene el campo único legado
        // cargado y todavía no migró a la lista de coberturas.
        patient.insurance_provider ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {patient.insurance_provider}{patient.insurance_policy_number ? ` — N° ${patient.insurance_policy_number}` : ''}
            {canEdit && ' (cargá una cobertura nueva abajo para poder editarla)'}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Sin coberturas cargadas.</Typography>
        )
      )}

      <Stack spacing={1} sx={{ mb: 1.5 }}>
        {insurances.map(ins => (
          <Box key={ins.id} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            {editingId === ins.id ? (
              <Stack spacing={1}>
                <Grid container spacing={1}>
                  <Grid size={12}>
                    <TextField fullWidth label="Prestador / Obra social" size="small" value={editForm.provider}
                      onChange={e => setEditForm(p => ({ ...p, provider: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="N° de afiliado" size="small" value={editForm.policy_number}
                      onChange={e => setEditForm(p => ({ ...p, policy_number: e.target.value }))} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label="Plan (opcional)" size="small" value={editForm.plan}
                      onChange={e => setEditForm(p => ({ ...p, plan: e.target.value }))} />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={editForm.is_primary}
                      onChange={e => setEditForm(p => ({ ...p, is_primary: e.target.checked }))} />}
                    label="Principal"
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={() => setEditingId(null)}>Cancelar</Button>
                    <Button size="small" variant="contained" disabled={savingEditId === ins.id || !editForm.provider.trim()}
                      onClick={() => handleSaveEdit(ins.id)}>
                      {savingEditId === ins.id ? <CircularProgress size={14} /> : 'Guardar'}
                    </Button>
                  </Box>
                </Box>
              </Stack>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={600}>{ins.provider}</Typography>
                    {ins.is_primary && <Chip label="Principal" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {ins.policy_number && `N° ${ins.policy_number}`}
                    {ins.plan && `${ins.policy_number ? ' · ' : ''}${ins.plan}`}
                  </Typography>
                </Box>
                {canEdit && (
                  <>
                    <IconButton size="small" onClick={() => startEdit(ins)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(ins)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                  </>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Stack>

      {canEdit && (
        adding ? (
          <Stack spacing={1} sx={{ p: 1.25, border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
            <Grid container spacing={1}>
              <Grid size={12}>
                <TextField fullWidth label="Prestador / Obra social *" size="small" value={newForm.provider} autoFocus
                  onChange={e => setNewForm(p => ({ ...p, provider: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="N° de afiliado" size="small" value={newForm.policy_number}
                  onChange={e => setNewForm(p => ({ ...p, policy_number: e.target.value }))} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Plan (opcional)" size="small" value={newForm.plan}
                  onChange={e => setNewForm(p => ({ ...p, plan: e.target.value }))} />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={newForm.is_primary}
                  onChange={e => setNewForm(p => ({ ...p, is_primary: e.target.checked }))} />}
                label="Marcar como principal"
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={() => setAdding(false)}>Cancelar</Button>
                <Button size="small" variant="contained" disabled={savingNew || !newForm.provider.trim()} onClick={handleAdd}>
                  {savingNew ? <CircularProgress size={14} /> : 'Agregar'}
                </Button>
              </Box>
            </Box>
          </Stack>
        ) : (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)}>Agregar cobertura</Button>
        )
      )}
    </Box>
  );
};

// ── Tab 0: Información del paciente ──────────────────────────────────────────
export const InfoTab = ({ patient, onUpdated }) => {
  const { isAdmin, isRecepcionista, isMedico } = useAuth();
  // Mismo criterio que PatientSidebar.js::canEditInfo — el backend ya
  // permite a médico editar pacientes, este guard interno se había quedado
  // desactualizado y dejaba los campos deshabilitados aun si se abría el
  // diálogo de edición. Reportado por el usuario 2026-10-12.
  const canEdit = isAdmin || isRecepcionista || isMedico;
  const [editing, setEditing] = useState(false);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    setForm({
      first_name: patient.first_name || '', last_name: patient.last_name || '',
      phone: patient.phone || '', email: patient.email || '',
      dob: patient.dob ? patient.dob.substring(0, 10) : '', address: patient.address || '',
      dni: patient.dni || '', gender: patient.gender || '', marital_status: patient.marital_status || '',
      blood_type: patient.blood_type || '', allergies: patient.allergies || '',
      chronic_diseases: patient.chronic_diseases || '', current_medications: patient.current_medications || '',
      insurance_provider: patient.insurance_provider || '', insurance_policy_number: patient.insurance_policy_number || '',
      emergency_contact_name: patient.emergency_contact_name || '',
      emergency_contact_phone: patient.emergency_contact_phone || '',
      emergency_contact_relationship: patient.emergency_contact_relationship || '',
      permanent_notes: patient.permanent_notes || '', is_active: patient.is_active,
    });
  }, [patient]);

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSave = async () => {
    setSaving(true); setError(''); setFieldErrors({});
    try { await updatePatient(patient.id, { ...form, dob: form.dob || null }); setEditing(false); onUpdated(); }
    catch (err) {
      const detail = err.response?.data?.detail;
      setFieldErrors(err.fieldErrors || {});
      if (err.response?.status === 409 && detail?.code === 'DUPLICATE_DNI') {
        setError(`Ya existe un paciente con ese DNI: ${detail.existing_name} (ID ${detail.existing_id}).`);
      } else {
        setError(err.userMessage || 'Error al guardar');
      }
    }
    finally { setSaving(false); }
  };

  const field = (label, key, props = {}) => (
    <TextField label={label} value={form[key] ?? ''} onChange={set(key)} disabled={!editing} fullWidth size="small"
      error={!!fieldErrors[key]} helperText={fieldErrors[key]} {...props} />
  );

  return (
    <Box sx={{ mt: 2 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          {editing ? (
            <>
              <Button startIcon={<CancelIcon />} onClick={() => { setEditing(false); setError(''); setFieldErrors({}); }}>Cancelar</Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={16} /> : 'Guardar cambios'}
              </Button>
            </>
          ) : (
            <Button startIcon={<EditIcon />} onClick={() => setEditing(true)}>Editar</Button>
          )}
        </Box>
      )}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Datos básicos</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>{field('Apellido *', 'last_name')}</Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{field('Nombre *', 'first_name')}</Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{field('DNI / Documento', 'dni')}</Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{field('Teléfono', 'phone')}</Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{field('Email', 'email')}</Grid>
            <Grid size={{ xs: 12, sm: 4 }}>{field('Fecha de nacimiento', 'dob', { type: 'date', InputLabelProps: { shrink: true } })}</Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small" disabled={!editing}>
                <InputLabel>Género</InputLabel>
                <Select value={form.gender || ''} label="Género" onChange={e => setForm(p => ({ ...p, gender: e.target.value }))}>
                  <MenuItem value=""><em>Sin especificar</em></MenuItem>
                  {GENDER_OPTIONS.map(g => <MenuItem key={g} value={g} sx={{ textTransform: 'capitalize' }}>{g}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>{field('Estado civil', 'marital_status')}</Grid>
            <Grid size={12}>{field('Domicilio', 'address')}</Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Información médica</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small" disabled={!editing}>
                <InputLabel>Grupo sanguíneo</InputLabel>
                <Select value={form.blood_type || ''} label="Grupo sanguíneo" onChange={e => setForm(p => ({ ...p, blood_type: e.target.value }))}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {BLOOD_TYPES.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 9 }}>{field('Alergias', 'allergies')}</Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {field('Antecedentes (texto libre, legado)', 'chronic_diseases', {
                helperText: 'Para condiciones crónicas activas, usá "Problemas" en la Historia Clínica — tiene estado y se puede filtrar.',
              })}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>{field('Medicación actual', 'current_medications')}</Grid>
            <Grid size={12}>{field('Notas permanentes', 'permanent_notes', { multiline: true, rows: 2 })}</Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded={(patient.insurances || []).length > 0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>
            Coberturas (obra social / prepaga)
            {(patient.insurances || []).length > 1 && ` — ${patient.insurances.length}`}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <InsurancesSection patient={patient} canEdit={canEdit} onChanged={onUpdated} />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Contacto de emergencia</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 5 }}>{field('Nombre', 'emergency_contact_name')}</Grid>
            <Grid size={{ xs: 12, sm: 4 }}>{field('Teléfono', 'emergency_contact_phone')}</Grid>
            <Grid size={{ xs: 12, sm: 3 }}>{field('Relación', 'emergency_contact_relationship')}</Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// ── Dialog para editar info del paciente ─────────────────────────────────────
export const EditPatientDialog = ({ open, onClose, patient, onUpdated }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle>
      Editar datos de {patient.name}
      <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CancelIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ p: 0 }}>
      <Box sx={{ p: 2 }}>
        <InfoTab patient={patient} onUpdated={() => { onUpdated(); onClose(); }} />
      </Box>
    </DialogContent>
  </Dialog>
);
